import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, Brain } from 'lucide-react';
import { apiClient } from '@/services/api';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface Chat {
  id: string;
  name: string;
  number: string;
  lastMessage: string;
  timestamp: number;
  unreadCount: number;
}

interface WhatsAppStatus {
  status: string;
  isReady: boolean;
  isAuthenticated: boolean;
}

export function WhatsAppWidget() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);

  const [view, setView] = useState<'chats' | 'memory'>('chats');
  const [memoryLogs, setMemoryLogs] = useState<any[]>([]);
  const [displayLimit, setDisplayLimit] = useState(20);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [view]);

  // Reset limit when view changes
  useEffect(() => {
    setDisplayLimit(20);
  }, [view]);

  const fetchData = async () => {
    try {
      if (view === 'chats') {
        const [statusRes, chatsData] = await Promise.all([
          apiClient.getWhatsAppStatus(),
          apiClient.getWhatsAppChats()
        ]);
        if (statusRes) setStatus(statusRes);
        if (chatsData) setChats(chatsData);
      } else {
        const logsData = await apiClient.getAIMemoryLogs();
        if (logsData) setMemoryLogs(logsData);
      }
    } catch (err) {
      console.error('Failed to fetch WhatsApp data', err);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipient || !newMessage) return;
    
    setSending(true);
    try {
      await apiClient.sendWhatsAppMessage(recipient, newMessage);
      setNewMessage('');
      fetchData();
    } catch (err) {
      console.error('Failed to send message', err);
    } finally {
      setSending(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    // Load more when scrolled near the bottom
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 100) {
      const currentListLength = view === 'chats' ? chats.length : memoryLogs.length;
      if (displayLimit < currentListLength) {
        setDisplayLimit(prev => Math.min(prev + 20, currentListLength));
      }
    }
  };

  const displayedChats = chats.slice(0, displayLimit);
  // Assuming memory logs might have newest at bottom, but if we scroll down to load more, it's 0-indexed.
  // We'll just slice the first N for infinite scroll down. 
  // If they want newest first, the backend should sort it. If backend sorts oldest first, it will show oldest.
  const displayedMemory = memoryLogs.slice(0, displayLimit);

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border h-full flex flex-col shadow-sm hover:shadow-md transition-all duration-300">
      <CardHeader className="pb-3 border-b border-border/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate('/whatsapp')}>
            <div className="w-8 h-8 bg-gradient-to-br from-[#25D366] to-[#128C7E] rounded-lg flex items-center justify-center shadow-sm">
              <MessageSquare className="h-4 w-4 text-white" />
            </div>
            WhatsApp AI
          </CardTitle>
          {status && (
            <Badge variant={status.isReady ? "default" : "secondary"} className={status.isReady ? "bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 border-none" : ""}>
              {status.isReady ? 'Connected' : 'Disconnected'}
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pt-4 flex-1 flex flex-col gap-4 p-4 min-h-0">
        {/* View Toggle */}
        <div className="flex gap-2 p-1 bg-accent/20 rounded-lg shrink-0">
          <Button 
            variant={view === 'chats' ? 'default' : 'ghost'} 
            size="sm" 
            onClick={() => setView('chats')}
            className={`flex-1 h-8 ${view === 'chats' ? 'bg-[#25D366] text-white hover:bg-[#128C7E]' : 'text-muted-foreground'}`}
          >
            <MessageSquare className="w-4 h-4 mr-2" /> Chats
          </Button>
          <Button 
            variant={view === 'memory' ? 'default' : 'ghost'} 
            size="sm" 
            onClick={() => setView('memory')}
            className={`flex-1 h-8 ${view === 'memory' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
          >
            <Brain className="w-4 h-4 mr-2" /> AI Memory
          </Button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto space-y-2 min-h-0 pr-2 custom-scrollbar" onScroll={handleScroll}>
          {view === 'chats' ? (
            chats.length > 0 ? (
              <>
                {displayedChats.map((chat) => (
                  <div 
                    key={chat.id} 
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-accent/50 cursor-pointer border border-transparent hover:border-border/50 transition-all duration-200" 
                    onClick={() => setRecipient(chat.number)}
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#25D366]/20 to-[#128C7E]/10 flex items-center justify-center flex-shrink-0 border border-[#25D366]/20">
                      <span className="font-semibold text-[#25D366]">{chat.name.charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-sm truncate text-foreground">{chat.name}</span>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap ml-2">
                          {formatDistanceToNow(chat.timestamp, { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{chat.lastMessage}</p>
                    </div>
                    {chat.unreadCount > 0 && (
                      <div className="w-5 h-5 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold text-white">{chat.unreadCount}</span>
                      </div>
                    )}
                  </div>
                ))}
                {displayLimit < chats.length && (
                  <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground my-2" onClick={() => setDisplayLimit(prev => Math.min(prev + 20, chats.length))}>
                    Load more chats
                  </Button>
                )}
              </>
            ) : (
               <div className="h-full flex flex-col items-center justify-center text-sm text-muted-foreground text-center space-y-3">
                  <MessageSquare className="w-8 h-8 text-muted-foreground/50" />
                  <p>{status?.isReady ? "No recent chats." : "WhatsApp is disconnected. Please link your device in Settings."}</p>
               </div>
            )
          ) : (
            memoryLogs.length > 0 ? (
              <>
                {displayedMemory.map((log, idx) => (
                  <div key={idx} className={`p-3 rounded-xl border border-border/50 space-y-1 ${
                    log.type === 'tool_call' || log.type === 'tool_result' ? 'bg-orange-500/10 border-orange-500/20' :
                    log.type === 'context' ? 'bg-blue-500/10 border-blue-500/20' :
                    log.role === 'ai' ? 'bg-primary/10 border-primary/20' : 'bg-accent/30'
                  }`}>
                    <div className="flex justify-between items-center mb-1">
                      <Badge variant="outline" className={`text-[10px] uppercase font-bold tracking-wider shrink-0 ${
                        log.role === 'ai' ? 'text-primary border-primary/50' : 
                        log.role === 'system' ? 'text-blue-400 border-blue-400/50' : 
                        'text-foreground/70'
                      }`}>
                        {log.type === 'tool_call' ? '🛠️ Tool Exec' : log.role}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap ml-2">
                        {formatDistanceToNow(log.timestamp, { addSuffix: true })}
                      </span>
                    </div>
                    <div className="text-xs text-foreground/90 whitespace-pre-wrap break-words break-all sm:break-words font-mono leading-relaxed">
                      {log.content}
                    </div>
                  </div>
                ))}
                {displayLimit < memoryLogs.length && (
                  <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground my-2" onClick={() => setDisplayLimit(prev => Math.min(prev + 20, memoryLogs.length))}>
                    Load more logs
                  </Button>
                )}
              </>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-sm text-muted-foreground text-center space-y-3">
                <Brain className="w-8 h-8 text-muted-foreground/50" />
                <p>No AI memory logs available.</p>
              </div>
            )
          )}
        </div>

        {/* Send Message Form */}
        <form onSubmit={handleSend} className="mt-auto space-y-3 pt-4 border-t border-border/50">
          <Input 
            placeholder="Recipient number (e.g. 26097...)" 
            value={recipient} 
            onChange={(e) => setRecipient(e.target.value)}
            className="bg-background/50 border-border focus:border-[#25D366] transition-colors"
            disabled={!status?.isReady || sending}
          />
          <div className="flex gap-2">
            <Input 
              placeholder="Type your message..." 
              value={newMessage} 
              onChange={(e) => setNewMessage(e.target.value)}
              className="flex-1 bg-background/50 border-border focus:border-[#25D366] transition-colors"
              disabled={!status?.isReady || sending}
            />
            <Button 
              type="submit" 
              disabled={!status?.isReady || sending || !recipient || !newMessage} 
              className="bg-[#25D366] hover:bg-[#128C7E] text-white shadow-md hover:shadow-lg transition-all"
            >
              <Send className="w-4 h-4 mr-2" />
              Send
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
